export const COMPLIANCE_STATUS_PARAMS = ['CONFORME', 'ALERTA', 'OFFLINE', 'INDISPONIVEL'] as const;
export const ALERT_TYPE_PARAMS = [
  'pre-offline',
  'high-oscillation',
  'average-oscillation',
  'execution-error',
  'operational-online',
] as const;

export type ComplianceStatusParam = (typeof COMPLIANCE_STATUS_PARAMS)[number];
export type AlertTypeParam = (typeof ALERT_TYPE_PARAMS)[number];

export interface DashboardScope {
  tenantId?: number;
  state?: string;
  materialName?: string;
  startDate?: string;
  endDate?: string;
}

export interface DrilldownFilters extends DashboardScope {
  complianceStatus?: ComplianceStatusParam;
  alertType?: AlertTypeParam;
}

export type DrilldownClick =
  | { kind: 'status-card'; complianceStatus: ComplianceStatusParam }
  | { kind: 'alert-row'; alertType: AlertTypeParam }
  | { kind: 'type-total'; materialName: string }
  | { kind: 'type-status'; materialName: string; complianceStatus: ComplianceStatusParam };

export type DrilldownIssueReason =
  | 'not-numeric'
  | 'unknown-state'
  | 'alert-type-requires-alert'
  | 'invalid-date'
  | 'start-after-end'
  | 'not-allowed';

export const PAGE_SIZES = [10, 20, 50] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const DEFAULT_LIST_STATE = {
  sortByTimeOnStatus: 'asc',
  sortByOscillation: 'desc',
  page: 1,
  pageSize: 10,
} as const;

export interface MaterialListState {
  search?: string;
  status?: string;
  sortByTimeOnStatus: SortDirection;
  sortByOscillation: SortDirection;
  page: number;
  pageSize: PageSize;
}

export interface DrilldownIssue {
  param: keyof DrilldownFilters | keyof MaterialListState | 'date' | 'from';
  reason: DrilldownIssueReason;
}

export interface ParsedDrilldownParams {
  filters: DrilldownFilters;
  list: MaterialListState;
  from?: 'dashboard';
  issues: DrilldownIssue[];
}

const BRAZILIAN_STATES: readonly string[] = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const isCalendarDate = (value: string) => {
  const time = Date.parse(`${value}T00:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(time) && new Date(time).toISOString().startsWith(value);
};

const MATERIALS_PATH = '/materials';
const DASHBOARD_PATH = '/dashboard';

const toQuery = (entries: [string, string | number | undefined][]) => {
  const params = new URLSearchParams();
  for (const [key, value] of entries) if (value !== undefined) params.set(key, String(value));
  return params.toString();
};

const scopeEntries = (scope: DashboardScope): [string, string | number | undefined][] => [
  ['tenantId', scope.tenantId],
  ['state', scope.state],
  ['materialName', scope.materialName],
  ['startDate', scope.startDate],
  ['endDate', scope.endDate],
];

export function buildDrilldownUrl(scope: DashboardScope, click: DrilldownClick): string {
  const filters: DrilldownFilters = { ...scope };
  if (click.kind === 'alert-row') {
    filters.complianceStatus = 'ALERTA';
    filters.alertType = click.alertType;
  } else {
    if (click.kind !== 'status-card') filters.materialName = click.materialName;
    if (click.kind !== 'type-total') filters.complianceStatus = click.complianceStatus;
  }

  const query = toQuery([
    ...scopeEntries(filters),
    ['complianceStatus', filters.complianceStatus],
    ['alertType', filters.alertType],
    ['from', 'dashboard'],
  ]);
  return `${MATERIALS_PATH}?${query}`;
}

export interface MaterialListLink extends DrilldownFilters {
  from?: 'dashboard';
  status?: string;
  search?: string;
  sortByTimeOnStatus?: SortDirection;
  sortByOscillation?: SortDirection;
  page?: number;
  pageSize?: PageSize;
}

export function buildMaterialListQuery(link: MaterialListLink): string {
  return toQuery([
    ...scopeEntries(link),
    ['complianceStatus', link.complianceStatus],
    ['alertType', link.alertType],
    ['from', link.from],
    ['status', link.status],
    ['search', link.search],
    ['sortByTimeOnStatus', link.sortByTimeOnStatus],
    ['sortByOscillation', link.sortByOscillation],
    ['page', link.page],
    ['pageSize', link.pageSize],
  ]);
}

export function resolveReturnUrl(from: unknown, href: string): string {
  return typeof from === 'string' && from.split('?')[0] === href ? from : href;
}

export function buildMaterialListUrl(baseUrl: string, link: MaterialListLink): string {
  return `${baseUrl}${MATERIALS_PATH}?${buildMaterialListQuery(link)}`;
}

export function parseDrilldownParams(search: string | URLSearchParams): ParsedDrilldownParams {
  const params = new URLSearchParams(search);
  const filters: DrilldownFilters = {};
  const issues: DrilldownIssue[] = [];
  const get = (key: string) => params.get(key) || undefined;
  const reject = (param: DrilldownIssue['param'], reason: DrilldownIssueReason) => issues.push({ param, reason });
  const oneOf = <T extends string>(
    param: DrilldownIssue['param'],
    value: string | undefined,
    allowed: readonly T[]
  ) => {
    if (value === undefined) return undefined;
    if ((allowed as readonly string[]).includes(value)) return value as T;
    reject(param, 'not-allowed');
    return undefined;
  };

  const tenantId = get('tenantId');
  if (tenantId !== undefined) {
    if (/^[1-9]\d*$/.test(tenantId) && Number.isSafeInteger(Number(tenantId))) filters.tenantId = Number(tenantId);
    else reject('tenantId', 'not-numeric');
  }

  const state = get('state')?.toUpperCase();
  if (state !== undefined) {
    if (BRAZILIAN_STATES.includes(state)) filters.state = state;
    else reject('state', 'unknown-state');
  }

  filters.materialName = get('materialName');

  const date = get('date');
  const rangeGiven = get('startDate') !== undefined || get('endDate') !== undefined;
  const validDate = (param: DrilldownIssue['param'], value: string | undefined) => {
    if (value === undefined) return undefined;
    if (isCalendarDate(value)) return value;
    reject(param, 'invalid-date');
    return undefined;
  };
  const startDate = rangeGiven ? validDate('startDate', get('startDate')) : validDate('date', date);
  const endDate = rangeGiven ? validDate('endDate', get('endDate')) : startDate;
  if (startDate && endDate && startDate > endDate) reject('startDate', 'start-after-end');
  else Object.assign(filters, { startDate, endDate });

  const complianceStatus = oneOf('complianceStatus', get('complianceStatus'), COMPLIANCE_STATUS_PARAMS);
  filters.complianceStatus = complianceStatus;

  const alertType = oneOf('alertType', get('alertType'), ALERT_TYPE_PARAMS);
  if (alertType !== undefined) {
    if (complianceStatus === 'ALERTA') filters.alertType = alertType;
    else reject('alertType', 'alert-type-requires-alert');
  }

  const from = oneOf('from', get('from'), ['dashboard'] as const);

  const pageParam = get('page');
  const pageValid = pageParam !== undefined && /^[1-9]\d*$/.test(pageParam) && Number.isSafeInteger(Number(pageParam));
  if (pageParam !== undefined && !pageValid) reject('page', 'not-numeric');

  const pageSize = oneOf('pageSize', get('pageSize'), PAGE_SIZES.map(String));
  const list: MaterialListState = {
    search: get('search'),
    status: get('status'),
    sortByTimeOnStatus:
      oneOf('sortByTimeOnStatus', get('sortByTimeOnStatus'), SORT_DIRECTIONS) ?? DEFAULT_LIST_STATE.sortByTimeOnStatus,
    sortByOscillation:
      oneOf('sortByOscillation', get('sortByOscillation'), SORT_DIRECTIONS) ?? DEFAULT_LIST_STATE.sortByOscillation,
    page: pageValid ? Number(pageParam) : DEFAULT_LIST_STATE.page,
    pageSize: pageSize ? (Number(pageSize) as PageSize) : DEFAULT_LIST_STATE.pageSize,
  };

  for (const key of Object.keys(filters) as (keyof DrilldownFilters)[]) {
    if (filters[key] === undefined) delete filters[key];
  }

  return { filters, list, from, issues };
}

export function buildDashboardBackUrl(search: string | URLSearchParams): string {
  const query = toQuery(scopeEntries(parseDrilldownParams(search).filters));
  return query ? `${DASHBOARD_PATH}?${query}` : DASHBOARD_PATH;
}
